import { useEffect, useState, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useSeoMeta } from '@/hooks/useSeoMeta';
import StatCard from './components/StatCard';
import TopPages from './components/TopPages';
import TopReferrers from './components/TopReferrers';
import HourlyChart from './components/HourlyChart';
import PostSchedule from './components/PostSchedule';

interface PageviewRow {
  page: string;
  referrer: string | null;
  session_id: string;
  created_at: string;
}

interface PageStat { page: string; count: number; }
interface ReferrerStat { referrer: string; count: number; }
interface HourlyData { hour: string; count: number; }

function buildHourlyBuckets(rows: PageviewRow[]): HourlyData[] {
  const now = new Date();
  const buckets: Record<string, number> = {};
  for (let i = 23; i >= 0; i--) {
    const d = new Date(now);
    d.setHours(d.getHours() - i, 0, 0, 0);
    const key = `${d.getHours().toString().padStart(2, '0')}:00`;
    buckets[key] = 0;
  }
  rows.forEach((r) => {
    const d = new Date(r.created_at);
    const key = `${d.getHours().toString().padStart(2, '0')}:00`;
    if (key in buckets) buckets[key]++;
  });
  return Object.entries(buckets).map(([hour, count]) => ({ hour, count }));
}

const FULL_REFRESH_INTERVAL = 2;
const LIVE_REFRESH_INTERVAL = 2;

export default function TrafficDashboard() {
  useSeoMeta({
    title: 'Traffic Dashboard — roameurope.blog',
    noindex: true,
  });

  const [loading, setLoading] = useState(true);
  const [totalViews, setTotalViews] = useState(0);
  const [todayViews, setTodayViews] = useState(0);
  const [liveVisitors, setLiveVisitors] = useState(0);
  const [topPages, setTopPages] = useState<PageStat[]>([]);
  const [topReferrers, setTopReferrers] = useState<ReferrerStat[]>([]);
  const [hourly, setHourly] = useState<HourlyData[]>([]);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [progress, setProgress] = useState(1); // 1 = full, 0 = empty
  const [liveFlash, setLiveFlash] = useState(false);
  const fetchStartRef = useRef<number>(Date.now());
  const isFetchingRef = useRef(false);

  const fetchData = useCallback(async () => {
    if (isFetchingRef.current) return;
    isFetchingRef.current = true;
    fetchStartRef.current = Date.now();
    setProgress(1);

    const now = new Date();
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate()).toISOString();
    const last24h = new Date(now.getTime() - 24 * 60 * 60 * 1000).toISOString();
    const live5min = new Date(now.getTime() - 5 * 60 * 1000).toISOString();

    const [allRes, todayRes, liveRes, last24Res] = await Promise.all([
      supabase.from('pageviews').select('id', { count: 'exact', head: true }),
      supabase.from('pageviews').select('id', { count: 'exact', head: true }).gte('created_at', todayStart),
      supabase.from('pageviews').select('session_id').gte('created_at', live5min),
      supabase.from('pageviews').select('page, referrer, session_id, created_at').gte('created_at', last24h),
    ]);

    setTotalViews(allRes.count ?? 0);
    setTodayViews(todayRes.count ?? 0);

    const liveSessions = new Set((liveRes.data ?? []).map((r) => r.session_id));
    setLiveVisitors(liveSessions.size);

    const rows: PageviewRow[] = last24Res.data ?? [];

    const pageMap: Record<string, number> = {};
    rows.forEach((r) => { pageMap[r.page] = (pageMap[r.page] ?? 0) + 1; });
    setTopPages(
      Object.entries(pageMap)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 8)
        .map(([page, count]) => ({ page, count }))
    );

    const refMap: Record<string, number> = {};
    rows.forEach((r) => {
      const key = r.referrer ?? '';
      refMap[key] = (refMap[key] ?? 0) + 1;
    });
    setTopReferrers(
      Object.entries(refMap)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 8)
        .map(([referrer, count]) => ({ referrer, count }))
    );

    setHourly(buildHourlyBuckets(rows));
    setLastRefresh(new Date());
    setLoading(false);
    setLiveFlash(true);
    setTimeout(() => setLiveFlash(false), 400);
    isFetchingRef.current = false;
  }, []);

  // Full data refresh every FULL_REFRESH_INTERVAL seconds
  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, FULL_REFRESH_INTERVAL * 1000);
    return () => clearInterval(interval);
  }, [fetchData]);

  // Smooth countdown progress bar — ticks every 50ms
  useEffect(() => {
    const tick = setInterval(() => {
      const elapsed = (Date.now() - fetchStartRef.current) / 1000;
      const remaining = Math.max(0, FULL_REFRESH_INTERVAL - elapsed);
      setProgress(remaining / FULL_REFRESH_INTERVAL);
    }, 50);
    return () => clearInterval(tick);
  }, []);

  const countdown = Math.ceil(progress * FULL_REFRESH_INTERVAL);

  const handleManualRefresh = () => {
    fetchData();
  };

  return (
    <div className="min-h-screen bg-stone-50 dark:bg-stone-950">
      {/* Header */}
      <div className="bg-white dark:bg-stone-900 border-b border-stone-100 dark:border-stone-800 px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/" className="text-stone-400 hover:text-stone-600 dark:hover:text-stone-300 transition-colors cursor-pointer">
            <i className="ri-arrow-left-line text-lg" />
          </Link>
          <div>
            <h1 className="text-base font-bold text-stone-800 dark:text-stone-100">Traffic Dashboard</h1>
            <p className="text-xs text-stone-400">
              Live visitors refresh every {LIVE_REFRESH_INTERVAL}s &middot; Full data every {FULL_REFRESH_INTERVAL}s
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {/* Countdown ring */}
          <div className="flex items-center gap-2 bg-stone-100 dark:bg-stone-800 rounded-full px-3 py-1.5">
            <svg className="w-4 h-4 -rotate-90" viewBox="0 0 20 20">
              <circle cx="10" cy="10" r="8" fill="none" stroke="#e7e5e4" strokeWidth="2.5" />
              <circle
                cx="10" cy="10" r="8"
                fill="none"
                stroke="#f43f5e"
                strokeWidth="2.5"
                strokeDasharray={`${2 * Math.PI * 8}`}
                strokeDashoffset={`${2 * Math.PI * 8 * (1 - progress)}`}
                strokeLinecap="round"
              />
            </svg>
            <span className="text-xs font-mono text-stone-500 dark:text-stone-400 w-5 text-center">
              {countdown}s
            </span>
          </div>
          <span className="text-xs text-stone-400 hidden sm:block">
            {lastRefresh.toLocaleTimeString()}
          </span>
          <a
            href="https://roameurope.blog/sitemap.xml"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1.5 text-xs bg-stone-100 hover:bg-stone-200 dark:bg-stone-800 dark:hover:bg-stone-700 text-stone-600 dark:text-stone-300 px-3 py-1.5 rounded-md transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-map-line" /> Sitemap
          </a>
          <button
            onClick={handleManualRefresh}
            className="flex items-center gap-1.5 text-xs bg-rose-500 hover:bg-rose-600 text-white px-3 py-1.5 rounded-md transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-refresh-line" /> Refresh
          </button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="flex flex-col items-center gap-3">
              <i className="ri-loader-4-line text-3xl text-rose-400 animate-spin" />
              <p className="text-sm text-stone-400">Loading traffic data...</p>
            </div>
          </div>
        ) : (
          <>
            {/* Live indicator */}
            <div className="flex items-center gap-2 mb-6">
              <span className="relative flex h-2.5 w-2.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                <span
                  className={`relative inline-flex rounded-full h-2.5 w-2.5 transition-colors duration-300 ${liveFlash ? 'bg-white' : 'bg-emerald-500'}`}
                />
              </span>
              <span className="text-xs font-medium text-emerald-600 dark:text-emerald-400">
                Live tracking active
              </span>
              <span className="text-xs text-stone-400">
                &mdash; visitors update every {LIVE_REFRESH_INTERVAL}s
              </span>
            </div>

            {/* Stat cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <StatCard
                label="Live Visitors"
                value={liveVisitors}
                icon="ri-user-line"
                sub={`Active in last 5 min · refreshes every ${LIVE_REFRESH_INTERVAL}s`}
                color="text-emerald-500"
              />
              <StatCard
                label="Today's Views"
                value={todayViews.toLocaleString()}
                icon="ri-eye-line"
                sub="Since midnight"
                color="text-rose-500"
              />
              <StatCard
                label="Last 24h Views"
                value={hourly.reduce((s, d) => s + d.count, 0).toLocaleString()}
                icon="ri-time-line"
                sub="Rolling 24 hours"
                color="text-amber-500"
              />
              <StatCard
                label="Total Pageviews"
                value={totalViews.toLocaleString()}
                icon="ri-bar-chart-line"
                sub="All time"
                color="text-violet-500"
              />
            </div>

            {/* Hourly chart */}
            <div className="mb-6">
              <HourlyChart data={hourly} />
            </div>

            {/* Top pages + referrers */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
              <TopPages data={topPages} total={hourly.reduce((s, d) => s + d.count, 0)} />
              <TopReferrers data={topReferrers} />
            </div>

            {/* Post Schedule */}
            <PostSchedule />
          </>
        )}
      </div>
    </div>
  );
}
